/**
 * irrigationRuns
 *
 * Turns the two live feeds the irrigation section already exposes —
 * `/irrigation/overview` (relay bitmasks, zone polygons, device positions) and
 * `/espnow/controllers/:id/schedule-state` (what each node says it is holding) —
 * into the handful of answers the landing page has to give at a glance: what is
 * watering, how long it has been going, and how much longer it has.
 *
 * Timing comes from the NODE's own clock frame. `secs_today` is the device's
 * seconds-from-midnight as at `reported_at`; advancing it by the wall-clock
 * time elapsed since then gives "now" in that same frame, which can then be
 * compared against `start_sec` directly. `runnable` is computed by the firmware
 * — never re-derive it here, or the UI drifts from the hardware.
 *
 * On/off is taken from the overview's relay bitmask, the same source the rest
 * of the irrigation UI uses, so this page can never disagree with the device
 * pages about whether a valve is open.
 *
 * A relay bitmask is only as good as its age. `relay_state_updated_at` is when
 * the controller last told the server that bitmask, and without it a zero bit
 * cannot be read at all: a valve reported off a second ago — its program did
 * not fire, and a block is dry — looks exactly like one nobody has heard from
 * since before the window opened. Those two get very different treatment below.
 *
 * Not every site keeps its timers on the nodes. An XBee site's laptop bridge
 * holds them instead: it asks the server for its programs every few minutes and
 * runs the clock itself, which is what keeps the farm watering through an
 * internet outage. Those nodes therefore report no armed windows at all, and
 * timing them from `schedule-state` gave the one answer that is never true of a
 * running farm — nothing watering, nothing due. For those controllers the
 * timing comes from `/espnow/controllers/:id/live-map` instead: today's steps
 * as the server holds them, read against the server clock it returns with them.
 * Same programs the bridge is running, same source the controller page's live
 * map already trusts.
 */

import type {
  GeoJSONPolygon,
  IrrigationOverviewData,
  OverviewController,
} from '../services/irrigationOverview';
import { isRelayOn } from '../services/irrigationOverview';
import { relayCountFor, relayDisplayName } from '../services/espnowIrrigation';
import type { EspNowRouter, LiveMapData, NodeScheduleState } from '../services/espnowIrrigation';
import { nodeState as reachability, type NodeState } from './nodeState';
import { sensorChannelHref } from './soilMoisture';

const DAY_SEC = 86400;

/**
 * Grace either side of a scheduled window before we stop believing an open
 * valve belongs to it. A node reports on its own cadence, so a run can look a
 * few seconds early or late without anything being wrong.
 */
const WINDOW_LEAD_GRACE_SEC = 30;
const WINDOW_TRAIL_GRACE_SEC = 120;

/**
 * A node posts a full schedule snapshot every 5 min, and immediately on config
 * apply, time sync and re-registration. Past three missed reports the numbers
 * in it stop describing what the hardware is about to do and become the last
 * thing it said before it went quiet — so we must not time runs from them. A
 * node that was removed, or a program that was deleted while its node was off,
 * leaves exactly such a row behind, and projecting it is how a phantom run
 * appears in "Up next" long after anything real was scheduled.
 */
const SCHEDULE_STALE_SEC = 15 * 60;

/**
 * How far past its start edge a queued run may sit before we stop calling it
 * "starting now" and roll it to the next day's edge. The relay schedule is
 * daily, and `next_run_in_sec` is the seconds to the next edge *as at the last
 * report* — so between reports a window that has already fired decays past
 * zero. Clamping that at zero pins a run at the current time forever; rolling
 * it forward gives the edge the node will actually act on next.
 */
const UPCOMING_OVERDUE_GRACE_SEC = 60;

/**
 * How long into a window a valve may still read as off before a *fresh* report
 * of that counts as a failure to start.
 *
 * A bridge commands the relay and the node reports back; the two are not
 * simultaneous, and a report posted in the first moments of a window can have
 * been sampled just before the valve closed. Past this, a controller that has
 * told us "off" since the window opened is telling us the program did not fire.
 */
export const VALVE_REPORT_GRACE_SEC = 60;

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Where a run's timing came from. `schedule` means the node's own armed window
 * explains it, so elapsed/remaining are real. `manual` means the valve is open
 * but no window accounts for it — a hand-run or an override — and the page must
 * say so rather than invent a countdown.
 */
export type RunTimingSource = 'schedule' | 'manual';

/**
 * Today's schedule for a site whose timers do not live on the nodes, as the
 * server holds it, plus the server clock it was read against.
 *
 * `nowSec` and every `start_sec`/`end_sec` in `timeline` share one frame, so
 * they are only ever compared with each other — never re-projected into a
 * timezone the payload does not carry. Advancing `nowSec` by the wall-clock
 * time since `fetchedAtMs` gives "now" in that frame, exactly the way
 * `projectNodeSecs` does for a node's own clock.
 */
export interface ControllerTimeline {
  timeline: LiveMapData['timeline'];
  /** Server seconds-from-midnight as at `fetchedAtMs`. */
  nowSec: number;
  fetchedAtMs: number;
  /**
   * The live-map's own router rows — the same ones the controller page draws
   * from. They carry the bridge's reachability verdict (`state`), which the
   * overview does not, and are read after the overview, so their relay
   * bitmask is never the older of the two.
   */
  routers?: EspNowRouter[];
}

export interface ZoneRun {
  /** `${router_mac}-${relay_index}` — stable across polls. */
  key: string;
  farmId: number | null;
  farmName: string;
  controllerId: string;
  controllerName: string;
  routerMac: string;
  routerName: string;
  /**
   * Reachability through `nodeState`, the same answer the device pages give.
   * Not the bare `online` boolean: on an XBee site that is false for any node
   * nobody has asked anything lately, and printing it as "offline" told the
   * operator a working node had failed.
   */
  routerState: NodeState;
  relayIndex: number;
  zoneName: string;
  zoneColor: string;
  /** The drawn area this valve waters, when someone has mapped it. */
  boundary: GeoJSONPolygon | null;
  lat: number | null;
  lng: number | null;
  source: RunTimingSource;
  /**
   * True when the device's own relay bitmask says this valve is open.
   *
   * False means only the site's schedule says so, and nothing has been heard
   * from the node since the window opened — genuinely unknown, not merely
   * unverified. A node that HAS reported since the window opened with the valve
   * still closed never reaches here: that is a program that did not fire, and
   * it is raised as an alert instead of shown as a run.
   */
  confirmed: boolean;
  /**
   * For an unconfirmed run: the bridge reported starting this sequence
   * (`SEQUENCE_STARTED`, which it sends only once the sequence's nodes have
   * answered their pre-flight). Not proof the valve is open — that is the relay
   * report still to come — but the site is running it, which is more than
   * "scheduled".
   */
  sequenceStarted: boolean;
  /** Null for a manual run — we genuinely do not know. */
  durationSec: number | null;
  elapsedSec: number | null;
  remainingSec: number | null;
  /** 0..1, or null when the duration is unknown. */
  progress: number | null;
}

export interface UpcomingRun {
  key: string;
  farmId: number | null;
  farmName: string;
  controllerId: string;
  routerMac: string;
  zoneName: string;
  zoneColor: string;
  startsInSec: number;
  /**
   * Wall-clock instant the run is due, as epoch ms. Derived from the countdown
   * rather than from `startSec`, because `startSec` lives in the node's own
   * frame (UTC plus the firmware's offset) and re-projecting it into the org
   * timezone would invent an accuracy the payload does not carry. Stable
   * across the 1 s tick: the countdown shrinks by exactly what `now` grows by.
   */
  startsAtMs: number;
  startSec: number;
  durationSec: number;
}

export interface IrrigationAlert {
  key: string;
  /** Null for a controller nobody has assigned to a farm yet. Lets the farm
   *  filter scope "Needs attention" the same way it scopes everything else. */
  farmId: number | null;
  severity: 'error' | 'warn';
  title: string;
  detail: string;
  /** Where to send someone who wants to act on it. */
  href: string;
}

export interface IrrigationLiveState {
  runs: ZoneRun[];
  upcoming: UpcomingRun[];
  alerts: IrrigationAlert[];
  /** Valves with a zone polygon or a schedule — i.e. ones we know about. */
  totalZones: number;
  routersOnline: number;
  routersTotal: number;
  controllersOffline: number;
  /** True when at least one controller answered with schedule state. */
  hasScheduleData: boolean;
  /**
   * True when at least one site's timings came from the server-held programs
   * instead of the nodes — i.e. a bridge site. Copy that says runs are "armed
   * on the nodes" is wrong for those, because nothing was ever pushed to them.
   */
  hasBridgeSchedule: boolean;
}

// ─── Time helpers ─────────────────────────────────────────────────────────────

/** Fold a seconds difference into ±12 h so a midnight straddle isn't a 24 h error. */
function foldDay(sec: number): number {
  let s = sec % DAY_SEC;
  if (s > DAY_SEC / 2) s -= DAY_SEC;
  if (s < -DAY_SEC / 2) s += DAY_SEC;
  return s;
}

/** Seconds of wall clock since a node reported, floored at zero. */
function secsSinceReport(reportedAt: string | null, nowMs: number): number | null {
  if (!reportedAt) return null;
  const at = Date.parse(reportedAt);
  if (Number.isNaN(at)) return null;
  return Math.max(0, (nowMs - at) / 1000);
}

/**
 * The node's own seconds-from-midnight, right now. Null when the node never
 * reported a clock — which is not the same as reporting midnight.
 */
export function projectNodeSecs(state: NodeScheduleState, nowMs: number): number | null {
  if (state.secs_today == null) return null;
  const drift = secsSinceReport(state.reported_at, nowMs);
  if (drift == null) return null;
  return state.secs_today + drift;
}

/**
 * The server's own seconds-from-midnight, right now, folded into a single day.
 *
 * The timeline is today's list in the server's day frame, so `nowSec` has to
 * stay inside that frame too — a snapshot read at 23:59:50 and ticked for
 * twenty seconds would otherwise read as 24:00:10 and put every step of the day
 * in the past.
 */
export function projectTimelineSecs(snapshot: ControllerTimeline, nowMs: number): number {
  const drift = Math.max(0, (nowMs - snapshot.fetchedAtMs) / 1000);
  return (((snapshot.nowSec + drift) % DAY_SEC) + DAY_SEC) % DAY_SEC;
}

/**
 * A UTC seconds-from-midnight value, re-expressed as seconds from LOCAL
 * midnight. `offsetSec` is the site's UTC offset (see tzOffsetSec).
 *
 * THE BUG THIS EXISTS FOR
 *   A farm with programs at 10 AM, 11 AM, 2 PM and 4 PM read "No further runs
 *   scheduled today" at 7 AM — with the whole day still ahead of it.
 *
 *   `start_time_sec` is seconds from midnight UTC and `days_of_week` is in
 *   LOCAL days. That pairing is correct and deliberate (see the bridge's
 *   test_schedule_timezone.py: move the weekday to UTC as well and a Monday
 *   9 AM program starts running on Sunday). But the two halves disagree about
 *   where a day starts, and comparing `start_sec > nowSec` LINEARLY assumes
 *   they agree.
 *
 *   On a +10 site at 07:00 local it is 21:00 the previous day in UTC, so
 *   nowSec is 75600 while today's runs are stored at 0, 3600, 14400 and 21600 —
 *   all numerically behind it, all actually still to come. Nothing satisfied
 *   `start_sec > nowSec`, so the page reported an empty day.
 *
 *   It is not wrong all day. It fails exactly while the local and UTC dates
 *   differ — midnight to 10 AM on a +10 site — which is the window in which
 *   somebody is most likely to be asking what the day holds.
 *
 * Folding both sides here puts them in the frame `days_of_week` already uses,
 * so "today" means one thing throughout. Display code is untouched and still
 * takes the raw UTC value: utcSecToLocalHHMM expects it.
 */
export function localDaySec(utcSec: number, offsetSec: number): number {
  return (((utcSec + offsetSec) % DAY_SEC) + DAY_SEC) % DAY_SEC;
}

/** One valve's windows in today's timeline, earliest first. */
export interface TimelineWindow {
  startSec: number;
  durationSec: number;
  /** The bridge reported this step starting — `timing: 'actual'`. */
  started: boolean;
}

/**
 * Every window today's programs open on one valve.
 *
 * A step's relays run simultaneously but each carries its own duration, so the
 * step's `end_sec` is when the *step* finishes, not when this valve closes.
 * Timing a run off the step would keep a two-minute valve on screen for the
 * length of a twenty-minute step.
 */
export function relayWindows(
  timeline: LiveMapData['timeline'],
  routerMac: string,
  relayIndex: number,
  // Folded here rather than at each comparison so every consumer of a window
  // is in one frame, and - just as importantly - so the SORT is. Ordering by
  // raw UTC seconds puts a 9 AM local run (23:00 UTC, 82800) last on a +10
  // site, behind every afternoon program.
  offsetSec = 0
): TimelineWindow[] {
  const windows: TimelineWindow[] = [];
  for (const step of timeline) {
    // A skipped sequence opened nothing. Its window would otherwise still span
    // the relay's duration from the moment of the skip, and the page would put
    // a dry block on screen as "should be watering".
    if (step.skipped) continue;
    for (const relay of step.relays) {
      if (relay.router_mac !== routerMac || relay.relay_index !== relayIndex) continue;
      if (relay.duration_sec <= 0) continue;
      windows.push({
        startSec: localDaySec(step.start_sec, offsetSec),
        durationSec: relay.duration_sec,
        started: step.timing === 'actual',
      });
    }
  }
  return windows.sort((a, b) => a.startSec - b.startSec);
}

/** "45s" / "12m 04s" / "1h 20m" — the shape a countdown wants. */
export function formatRunTime(sec: number): string {
  const total = Math.max(0, Math.round(sec));
  if (total < 60) return `${total}s`;
  if (total < 3600) {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}m ${String(s).padStart(2, '0')}s`;
  }
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/** "12m" / "3h" / "4 days" — coarse enough to read as an age, not a countdown. */
function formatAge(sec: number | null): string {
  if (sec == null) return 'an unknown time';
  if (sec < 3600) return `${Math.max(1, Math.round(sec / 60))}m`;
  if (sec < DAY_SEC) return `${Math.round(sec / 3600)}h`;
  const days = Math.round(sec / DAY_SEC);
  return `${days} day${days === 1 ? '' : 's'}`;
}

// ─── Derivation ───────────────────────────────────────────────────────────────

interface ControllerContext {
  controller: OverviewController;
  farmId: number | null;
  farmName: string;
}

/**
 * Collapse farms and unassigned devices into one list, so a valve still shows
 * up on the landing page before anyone has assigned its controller to a farm.
 */
function controllerContexts(data: IrrigationOverviewData): ControllerContext[] {
  const contexts: ControllerContext[] = [];
  for (const farm of data.farms) {
    for (const controller of farm.controllers) {
      contexts.push({ controller, farmId: farm.id, farmName: farm.name });
    }
  }
  for (const controller of data.unassigned.controllers) {
    contexts.push({ controller, farmId: null, farmName: 'Unassigned' });
  }
  return contexts;
}

export function buildLiveState(
  data: IrrigationOverviewData | null,
  scheduleByController: Record<string, NodeScheduleState[]>,
  timelineByController: Record<string, ControllerTimeline>,
  nowMs: number,
  // The site's UTC offset in seconds, for folding the bridge timeline's UTC
  // times into the local day that `days_of_week` selected them for. See
  // localDaySec. Defaults to 0 so a caller without a timezone gets the old
  // UTC-frame behaviour rather than a silently shifted one.
  tzOffsetSec = 0
): IrrigationLiveState {
  const empty: IrrigationLiveState = {
    runs: [],
    upcoming: [],
    alerts: [],
    totalZones: 0,
    routersOnline: 0,
    routersTotal: 0,
    controllersOffline: 0,
    hasScheduleData: false,
    hasBridgeSchedule: false,
  };
  if (!data) return empty;

  const runs: ZoneRun[] = [];
  const upcoming: UpcomingRun[] = [];
  const alerts: IrrigationAlert[] = [];
  let totalZones = 0;
  let routersOnline = 0;
  let routersTotal = 0;
  let controllersOffline = 0;
  let hasScheduleData = false;
  let hasBridgeSchedule = false;

  for (const { controller, farmId, farmName } of controllerContexts(data)) {
    const nodeStates = scheduleByController[controller.controller_id];
    if (nodeStates && nodeStates.length > 0) hasScheduleData = true;

    // A site whose timers live off the nodes. Present only for those, so its
    // presence is what selects the timing source below.
    const timelineSnap = timelineByController[controller.controller_id];
    // Folded into the LOCAL day, the same frame the windows below are folded
    // into. Both sides must move together or the comparison is worse than the
    // one it replaced - see localDaySec.
    const timelineNow = timelineSnap
      ? localDaySec(projectTimelineSecs(timelineSnap, nowMs), tzOffsetSec)
      : null;
    if (timelineSnap) hasBridgeSchedule = true;

    // A skipped sequence is water that did not happen today. It has no run card
    // (relayWindows drops it), so this alert is the only place it shows - and
    // it names the node, because that is where somebody has to go.
    for (const step of timelineSnap?.timeline ?? []) {
      if (!step.skipped) continue;
      const names = (step.silent_nodes ?? []).map(
        (mac) => controller.routers.find((r) => r.router_mac === mac)?.name || mac
      );
      alerts.push({
        key: `skipped-${controller.controller_id}-${step.program_id}-${step.start_id ?? ''}-${step.step_id}`,
        farmId,
        severity: 'error',
        title: `Sequence ${step.step_number ?? step.step_order} of ${step.program_name} was skipped`,
        detail: names.length
          ? `${names.join(', ')} did not answer before it was due, so none of its valves were opened.`
          : 'A node did not answer before it was due, so none of its valves were opened.',
        href: `/irrigation/espnow/${controller.controller_id}`,
      });
    }

    const stateByMac: Record<string, NodeScheduleState> = {};
    for (const s of nodeStates ?? []) stateByMac[s.router_mac] = s;

    // Somebody stopped this site and has not resumed it. A warning rather than
    // an error: it is a deliberate state, and colouring it red would put a farm
    // that is behaving exactly as instructed above a node that has actually
    // failed. But it has to be HERE, because the rest of the page reports a
    // held farm as "no valves are open" — true, and the single most misleading
    // silence this screen can produce.
    if (controller.watering_paused_at) {
      alerts.push({
        key: `watering-held-${controller.controller_id}`,
        farmId,
        severity: 'warn',
        title: `Watering is stopped at ${controller.controller_name || controller.controller_id}`,
        detail: controller.watering_pause_reason
          ? `No program will run until it is resumed — “${controller.watering_pause_reason}”.`
          : 'No program will run here, including tonight’s, until somebody resumes it.',
        href: `/irrigation/espnow/${controller.controller_id}`,
      });
    }

    if (!controller.online) {
      controllersOffline++;
      alerts.push({
        key: `controller-offline-${controller.controller_id}`,
        farmId,
        severity: 'error',
        title: `${controller.controller_name || controller.controller_id} is offline`,
        detail: 'Valves on this controller cannot be switched or reported until it reconnects.',
        href: `/irrigation/espnow/${controller.controller_id}`,
      });
    }

    // A bridge site's live-map rows are the controller page's rows. Prefer them
    // per MAC, so this page reads a node's valves and reachability exactly as
    // that page does, rather than from a second, thinner copy.
    const liveByMac = new Map((timelineSnap?.routers ?? []).map((r) => [r.router_mac, r]));

    for (const overviewRouter of controller.routers) {
      const liveRow = liveByMac.get(overviewRouter.router_mac);
      const router = liveRow
        ? {
            ...overviewRouter,
            online: liveRow.online,
            relay_states: liveRow.relay_states,
            relay_state_updated_at: liveRow.relay_state_updated_at,
          }
        : overviewRouter;
      // The overview row carries no `state`, so its boolean is mapped back the
      // way the server maps it for routers with no verdict: a missed heartbeat
      // is real evidence on ESP-NOW, while a quiet XBee node is merely unasked.
      const routerState: NodeState = liveRow
        ? reachability(liveRow)
        : router.online
          ? 'online'
          : controller.transport === 'xbee'
            ? 'unknown'
            : 'offline';

      routersTotal++;
      if (routerState === 'online') routersOnline++;

      const nodeState = stateByMac[router.router_mac];
      const nodeNow = nodeState ? projectNodeSecs(nodeState, nowMs) : null;
      const sinceReport = nodeState ? secsSinceReport(nodeState.reported_at, nowMs) : null;

      const zonesForRouter = controller.zones.filter((z) => z.router_mac === router.router_mac);
      const zoneByRelay = new Map(zonesForRouter.map((z) => [z.relay_index, z]));

      if (nodeState && !nodeState.clock_ok) {
        alerts.push({
          key: `no-clock-${router.router_mac}`,
          farmId,
          severity: 'error',
          title: `${router.name || router.router_mac} has no clock`,
          detail: 'Nothing on this node will actuate on schedule until its time is set.',
          href: `/irrigation/espnow/${controller.controller_id}`,
        });
      }

      // Too old to time anything from. Say so rather than silently dropping the
      // node's runs — "nothing scheduled" and "we have not heard from the thing
      // that would tell us" are different answers.
      const scheduleStale =
        nodeState != null && (sinceReport == null || sinceReport > SCHEDULE_STALE_SEC);
      if (scheduleStale && nodeState.relays.some((r) => r.runnable)) {
        alerts.push({
          key: `stale-schedule-${router.router_mac}`,
          farmId,
          severity: 'warn',
          title: `${router.name || router.router_mac} has not reported for ${formatAge(sinceReport)}`,
          detail:
            'Its last schedule snapshot is too old to time runs from, so its programs are not shown under "Up next". If this node has been removed, clear it from the controller.',
          href: `/irrigation/espnow/${controller.controller_id}`,
        });
      }

      // Only the relays the board has. An XBee node reports no capability
      // counts, so without the transport default this walked eight every time
      // and a 4-relay board grew four zones nothing can ever open.
      const relayCount = relayCountFor(router, controller.transport);
      for (let relayIndex = 0; relayIndex < relayCount; relayIndex++) {
        const zone = zoneByRelay.get(relayIndex);
        const relaySched = nodeState?.relays.find((r) => r.relay === relayIndex);
        const windows = timelineSnap
          ? relayWindows(timelineSnap.timeline, router.router_mac, relayIndex, tzOffsetSec)
          : [];
        if (zone || relaySched?.enabled || windows.length > 0) totalZones++;

        const on = isRelayOn(router.relay_states, relayIndex);
        const zoneName = relayDisplayName(zone?.zone_name, router.relay_labels, relayIndex);
        const zoneColor = zone?.zone_color || '#3B82F6';

        /** Everything about the valve that does not depend on how it is timed. */
        const runBase = {
          key: `${router.router_mac}-${relayIndex}`,
          farmId,
          farmName,
          controllerId: controller.controller_id,
          controllerName: controller.controller_name || controller.controller_id,
          routerMac: router.router_mac,
          routerName: router.name || router.router_mac,
          routerState,
          relayIndex,
          sequenceStarted: false,
          zoneName,
          zoneColor,
          boundary: zone?.boundary_geometry ?? null,
          lat: router.lat,
          lng: router.lng,
        };

        // ── Bridge site: the server's own programs are the schedule ──────────
        // Nothing was pushed to these nodes, so there is no armed window to
        // read. Today's timeline and the server clock it came with answer both
        // questions the page asks, and answer them for a valve whose state the
        // node has not reported yet — the case that used to read as "nothing
        // watering" on a farm that was watering.
        if (timelineSnap && timelineNow != null) {
          const active =
            windows.find(
              (w) => timelineNow >= w.startSec && timelineNow < w.startSec + w.durationSec
            ) ?? null;

          if (on) {
            const elapsedSec = active ? timelineNow - active.startSec : null;
            runs.push({
              ...runBase,
              source: active ? 'schedule' : 'manual',
              confirmed: true,
              durationSec: active?.durationSec ?? null,
              elapsedSec,
              remainingSec: active ? active.durationSec - (elapsedSec as number) : null,
              progress: active ? Math.min(1, (elapsedSec as number) / active.durationSec) : null,
            });
            continue;
          }

          if (active) {
            // The window is open but the bitmask says the valve is not. Whether
            // that is an alarm or simply unknown turns entirely on the age of
            // the bitmask — which is why the overview carries one.
            const elapsedSec = timelineNow - active.startSec;
            const windowStartMs = nowMs - elapsedSec * 1000;
            const reportedAtMs = router.relay_state_updated_at
              ? Date.parse(router.relay_state_updated_at)
              : NaN;
            const toldSinceStart =
              Number.isFinite(reportedAtMs) &&
              reportedAtMs >= windowStartMs + VALVE_REPORT_GRACE_SEC * 1000;

            if (toldSinceStart) {
              // The controller has reported this node since the window opened
              // and the valve is off in that report. The program did not fire,
              // and a block is dry — that belongs in "needs attention", not on
              // a run card dressed up as water on the ground.
              alerts.push({
                key: `did-not-start-${router.router_mac}-${relayIndex}`,
                farmId,
                severity: 'error',
                title: `${zoneName} did not start`,
                detail: `Its program has been running for ${formatRunTime(elapsedSec)}, but ${
                  router.name || router.router_mac
                } reports the valve closed. Nothing is watering here.`,
                href: `/irrigation/espnow/${controller.controller_id}`,
              });
              continue;
            }

            runs.push({
              ...runBase,
              source: 'schedule',
              confirmed: false,
              sequenceStarted: active.started,
              durationSec: active.durationSec,
              elapsedSec,
              remainingSec: active.durationSec - elapsedSec,
              progress: Math.min(1, elapsedSec / active.durationSec),
            });
            continue;
          }

          const next = windows.find((w) => w.startSec > timelineNow);
          if (next) {
            const startsInSec = next.startSec - timelineNow;
            upcoming.push({
              key: `${router.router_mac}-${relayIndex}`,
              farmId,
              farmName,
              controllerId: controller.controller_id,
              routerMac: router.router_mac,
              zoneName,
              zoneColor,
              startsInSec,
              startsAtMs: nowMs + startsInSec * 1000,
              startSec: next.startSec,
              durationSec: next.durationSec,
            });
          }
          continue;
        }

        if (on) {
          // Does an armed window explain this valve being open? If so the
          // node's own numbers give us elapsed and remaining for free.
          let source: RunTimingSource = 'manual';
          let durationSec: number | null = null;
          let elapsedSec: number | null = null;
          let remainingSec: number | null = null;

          if (relaySched && relaySched.duration_sec > 0 && nodeNow != null) {
            const offset = foldDay(nodeNow - relaySched.start_sec);
            const inWindow =
              offset >= -WINDOW_LEAD_GRACE_SEC &&
              offset <= relaySched.duration_sec + WINDOW_TRAIL_GRACE_SEC;
            if (inWindow) {
              source = 'schedule';
              durationSec = relaySched.duration_sec;
              elapsedSec = Math.min(Math.max(offset, 0), relaySched.duration_sec);
              remainingSec = Math.max(0, relaySched.duration_sec - offset);
            }
          }

          runs.push({
            ...runBase,
            source,
            confirmed: true,
            durationSec,
            elapsedSec,
            remainingSec,
            progress:
              durationSec && durationSec > 0 && elapsedSec != null
                ? Math.min(1, elapsedSec / durationSec)
                : null,
          });
          continue;
        }

        if (!relaySched) continue;

        // Stored but the node says it will not water — the difference between
        // "saved" and "will actually run", and the one worth surfacing.
        if (relaySched.enabled && !relaySched.runnable) {
          alerts.push({
            key: `wont-run-${router.router_mac}-${relayIndex}`,
            farmId,
            severity: 'warn',
            title: `${zoneName} will not run`,
            detail:
              relaySched.reason ||
              'The node accepted the program but reports it as not armed.',
            href: `/irrigation/espnow/${controller.controller_id}`,
          });
          continue;
        }

        if (relaySched.runnable && relaySched.next_run_in_sec != null && !scheduleStale) {
          const dueIn = relaySched.next_run_in_sec - (sinceReport ?? 0);
          const startsInSec =
            dueIn >= -UPCOMING_OVERDUE_GRACE_SEC
              ? Math.max(0, dueIn)
              : ((dueIn % DAY_SEC) + DAY_SEC) % DAY_SEC;
          upcoming.push({
            key: `${router.router_mac}-${relayIndex}`,
            farmId,
            farmName,
            controllerId: controller.controller_id,
            routerMac: router.router_mac,
            zoneName,
            zoneColor,
            startsInSec,
            startsAtMs: nowMs + startsInSec * 1000,
            startSec: relaySched.start_sec,
            durationSec: relaySched.duration_sec,
          });
        }
      }
    }
  }

  // Low tanks are the other thing that stops water arriving.
  for (const farm of data.farms) {
    for (const sensor of farm.sensors) {
      if (sensor.type_key !== 'tank_level') continue;
      if (sensor.latest_value == null || sensor.latest_value >= 25) continue;
      alerts.push({
        key: `low-tank-${sensor.device_id}`,
        farmId: farm.id,
        severity: 'warn',
        title: `${sensor.name || sensor.device_id} is low`,
        detail: `${sensor.latest_value.toFixed(0)}% remaining on ${farm.name}.`,
        href: `/irrigation/sensors/${sensor.device_id}`,
      });
    }
  }

  // A dry block is the sensor reading that costs a crop, so it is an error, not
  // a warning. The judgement is the server's (soil_channels[].status).
  for (const farm of data.farms) {
    for (const sensor of farm.sensors) {
      for (const ch of sensor.soil_channels ?? []) {
        const name = ch.name?.trim()
          ? `${sensor.name || sensor.device_id} · ${ch.name.trim()}`
          : sensor.name || sensor.device_id;
        const unit = ch.type_unit ? ` ${ch.type_unit}` : '';
        const value = ch.latest_value == null ? null : Number(ch.latest_value).toFixed(0);
        const href = sensorChannelHref(sensor.device_id, ch.channel_kind, ch.channel_index);
        const key = `soil-${sensor.device_id}-${ch.channel_kind}-${ch.channel_index}`;
        const quiet = ch.stale ? ' Its last reading is old, so the block may be drier now.' : '';
        if (ch.status === 'dry' && ch.levels) {
          alerts.push({
            key,
            farmId: farm.id,
            severity: 'error',
            title: `${name} is dry`,
            detail: `Reads ${value}${unit}, past its dry level of ${ch.levels.dry}${unit} on ${farm.name}.${quiet}`,
            href,
          });
        } else if (ch.status === 'drying' && ch.levels) {
          alerts.push({
            key,
            farmId: farm.id,
            severity: 'warn',
            title: `${name} needs water`,
            detail: `Reads ${value}${unit}, past its refill level of ${ch.levels.refill}${unit}.${quiet}`,
            href,
          });
        } else if (ch.stale && ch.levels) {
          alerts.push({
            key,
            farmId: farm.id,
            severity: 'warn',
            title: `${name} has gone quiet`,
            detail: 'No soil reading for a while, so nothing here would say if the block went dry.',
            href,
          });
        }
      }
    }
  }

  // Confirmed valves first — a valve the hardware says is open outranks one the
  // schedule only says should be — then longest-running, because the run closest
  // to finishing is the one an operator is usually waiting on.
  runs.sort(
    (a, b) =>
      Number(b.confirmed) - Number(a.confirmed) || (b.elapsedSec ?? -1) - (a.elapsedSec ?? -1)
  );
  upcoming.sort((a, b) => a.startsInSec - b.startsInSec);
  alerts.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'error' ? -1 : 1));

  return {
    runs,
    upcoming,
    alerts,
    totalZones,
    routersOnline,
    routersTotal,
    controllersOffline,
    hasScheduleData,
    hasBridgeSchedule,
  };
}
